import { convertString } from 'converter';
import { FieldProps, Field } from 'formik';
import React, { useCallback } from 'react';
import {
  FormPageInputItemMailAddress,
  MailAddressProperty,
  FormPageInputData,
  MAIL_ADDRESS_PROPERTY_INPUT_TYPE,
} from 'shared';
import { FormFieldProps } from '../shared';
import { useFieldPropsHandler } from '../useFieldPropsHandler';
import { MailAddressInput, MailAddressInputProps } from './MailAddressInput';

type Props = FormFieldProps<MailAddressProperty>;

const Container: React.FC<
  Props & {
    fieldProps: FieldProps<FormPageInputItemMailAddress, FormPageInputData>;
  }
> = ({ fieldProps, property, design, disabled, submitted, reEnter }) => {
  const { propertyId, stringConvertRule, inputFormat } = property;
  const {
    fieldTouched,
    fieldErrorMessages,
    updateFieldValue,
    updateFieldTouched,
  } = useFieldPropsHandler<FormPageInputItemMailAddress>({
    fieldProps,
    propertyId,
  });
  const handleChange: MailAddressInputProps['onChange'] = useCallback(
    (key, updateValue) => {
      updateFieldValue(key, updateValue);
    },
    [updateFieldValue]
  );
  const handleBlur: MailAddressInputProps['onBlur'] = useCallback(
    (key, updateValue) => {
      const domainPulldownAndKeyIsDomain =
        inputFormat.inputType ===
          MAIL_ADDRESS_PROPERTY_INPUT_TYPE.domainPulldown &&
        (key as 'local' | 'domain') === 'domain';
      if (domainPulldownAndKeyIsDomain) {
        updateFieldValue(key, updateValue);
        updateFieldTouched(key);
        return;
      }
      const convertValue = convertString(stringConvertRule);
      const convertedValue = convertValue(updateValue) as typeof updateValue;
      updateFieldValue(key, convertedValue);
      updateFieldTouched(key);
    },
    [
      updateFieldValue,
      updateFieldTouched,
      stringConvertRule,
      inputFormat.inputType,
    ]
  );

  if (fieldProps.field.value == null) {
    return null;
  }

  return (
    <MailAddressInput
      property={property}
      design={design}
      disabled={disabled}
      value={fieldProps.field.value}
      onChange={handleChange}
      onBlur={handleBlur}
      errorMessages={fieldErrorMessages}
      touched={fieldTouched}
      submitted={submitted}
      reEnter={reEnter}
    />
  );
};

export const MailAddress: React.FC<Props> = (props) => {
  return (
    <Field name={props.property.propertyId}>
      {(fieldProps: FieldProps<FormPageInputItemMailAddress>) => (
        <Container {...props} fieldProps={fieldProps} />
      )}
    </Field>
  );
};
